// The platform's one frame grammar, read from the browser: every streaming
// reply on the platform is one request with a reply inbox, answered by frames
// on that inbox (02-DESIGN/05-inference/02-the-inference-contract.md § the
// frame grammar; decision 0011). The inference contract defines it; the tools
// contract, the configuration watch and the state history and watch
// instantiate it and define nothing of their own. This module is the reader
// only: a browser is a client and writes no frames.
//
// A content frame is non-empty, sealed toward the caller, and carries
// Impire-Seq from 1, monotonic by one. A progress frame is empty and carries
// Impire-Progress: true. The terminator is empty and carries Impire-Status —
// done or error — and the Impire-Seq of the last content frame. The first
// frame carries Impire-Request, the id the serving instance minted. Errors are
// terminal and in clear: the micro error headers, no body. A one-shot answer
// is one non-empty reply with the terminator's headers and no Impire-Seq; one
// that would exceed the payload limit streams regardless.
import { headers as newHeaders, NoRespondersError, } from "@nats-io/nats-core";
import { CODE_CANNOT_OPEN, DEFAULT_TIMEOUT, ERROR_CODE_HEADER, ERROR_HEADER, HEADER as SEALED_BY, ServiceError, } from "./envelope.js";
import { newInbox } from "./inbox.js";
/** HEADER_SEQ is the sequence of a content frame, from 1 by one; on the terminator, the sequence of the last content frame. */
export const HEADER_SEQ = "Impire-Seq";
/** HEADER_PROGRESS marks an empty keepalive frame: the stream continues. */
export const HEADER_PROGRESS = "Impire-Progress";
/** HEADER_STATUS marks the terminator: done or error. Nothing follows it. */
export const HEADER_STATUS = "Impire-Status";
/** HEADER_REQUEST rides the first frame: the request id the serving instance minted, which a caller cancels by and a record names. */
export const HEADER_REQUEST = "Impire-Request";
/** The header values. */
export const STATUS_DONE = "done";
export const STATUS_ERROR = "error";
export const PROGRESS_TRUE = "true";
/** DEFAULT_IDLE is how long a reader waits for the next frame before it abandons the stream, in milliseconds: twice the reference progress interval. */
export const DEFAULT_IDLE = 60_000;
/** The server's no-responders status, delivered to the reply inbox when nothing serves the subject. */
const NO_RESPONDERS = 503;
/**
 * GapError is thrown when a sequence is skipped or the terminator's sequence
 * is not the last content frame's: inbox delivery is at most once, so a gap is
 * a failure, never a short answer.
 */
export class GapError extends Error {
    constructor(message = "frame: sequence gap") {
        super(message);
        this.name = "GapError";
    }
}
/** IdleError is thrown when no frame arrived within the idle deadline. */
export class IdleError extends Error {
    constructor(message = "frame: stream idle") {
        super(message);
        this.name = "IdleError";
    }
}
/** ProtocolError is thrown for a reply that is not a frame of the grammar. */
export class ProtocolError extends Error {
    constructor(message = "frame: not a frame") {
        super(message);
        this.name = "ProtocolError";
    }
}
/** Kind is what a frame is. */
export const Kind = {
    /** One piece of the reply, opened. A one-shot answer is one content frame with sequence zero. */
    content: "content",
    /** An empty keepalive; the family's own headers may ride it. */
    progress: "progress",
    /** The end, done. An error terminator is thrown as a ServiceError. */
    terminator: "terminator",
};
const EMPTY = new Uint8Array(0);
/** abortReason is what an aborted signal throws: its reason when that is an error, an AbortError otherwise. */
function abortReason(signal) {
    const reason = signal.reason;
    return reason instanceof Error ? reason : new DOMException("frame: aborted", "AbortError");
}
/**
 * next waits for the next message within the idle deadline and the signal. A
 * loss of the race is terminal for the reader, so the message the pending
 * iterator may later resolve with is never read by anyone.
 */
async function next(it, idle, signal) {
    if (signal?.aborted) {
        throw abortReason(signal);
    }
    let timer;
    let onAbort;
    try {
        const result = await Promise.race([
            it.next(),
            new Promise((_, reject) => {
                timer = setTimeout(() => {
                    reject(new IdleError());
                }, idle);
            }),
            new Promise((_, reject) => {
                if (signal) {
                    onAbort = () => {
                        reject(abortReason(signal));
                    };
                    signal.addEventListener("abort", onAbort, { once: true });
                }
            }),
        ]);
        if (result.done === true) {
            throw new ProtocolError("frame: the inbox subscription closed");
        }
        return result.value;
    }
    finally {
        clearTimeout(timer);
        if (signal && onAbort) {
            signal.removeEventListener("abort", onAbort);
        }
    }
}
function parseSeq(s) {
    if (!/^\d+$/.test(s)) {
        return undefined;
    }
    const n = Number(s);
    return Number.isSafeInteger(n) ? n : undefined;
}
/** Reader reads the frames of one reply. */
export class Reader {
    /** The id the first frame carried. */
    request;
    /** Whether the reply is a single message — known from the first frame, which open has read — or frames to be read by sequence. */
    oneShot;
    sub;
    it;
    caller;
    /** The key the request was sealed toward: every content frame is opened against it, whatever its header names (decision 0026 point 5). */
    key;
    idle;
    signal;
    first;
    seq = 0;
    term;
    done = false;
    constructor(sub, it, caller, key, idle, signal, first) {
        this.sub = sub;
        this.it = it;
        this.caller = caller;
        this.key = key;
        this.idle = idle;
        this.signal = signal;
        this.first = first;
        this.request = first.headers?.get(HEADER_REQUEST) ?? "";
        this.oneShot = (first.headers?.get(HEADER_SEQ) ?? "") === "" && first.data.length > 0;
    }
    /**
     * terminator is the terminator's headers — the family's stop reason, usage,
     * revision — undefined until the stream ended. On a one-shot reply it is the
     * one reply's headers.
     */
    terminator() {
        return this.term;
    }
    // ended reports whether the stream ended; a method, because classify ends it
    // out of the loop's sight.
    ended() {
        return this.done;
    }
    /** close drops the inbox subscription; frames does it when the loop ends. */
    close() {
        this.sub.unsubscribe();
    }
    /**
     * frames yields the frames in order: content opened and checked for a gap,
     * progress, and the done terminator last. An error terminator ends the
     * sequence with a ServiceError carrying its code; a gap with GapError;
     * an abort with the signal's reason; silence past the idle deadline with
     * IdleError. The loop ending, by the terminator or by the caller breaking
     * out, closes the reader.
     */
    async *frames() {
        try {
            if (this.done) {
                return;
            }
            let msg = this.first;
            this.first = undefined;
            for (;;) {
                if (msg === undefined) {
                    try {
                        msg = await next(this.it, this.idle, this.signal);
                    }
                    catch (err) {
                        this.done = true;
                        throw err;
                    }
                }
                let f;
                try {
                    f = this.classify(msg);
                }
                catch (err) {
                    this.done = true;
                    throw err;
                }
                msg = undefined;
                yield f;
                if (this.ended()) {
                    return;
                }
            }
        }
        finally {
            this.close();
        }
    }
    /** classify reads one reply as a frame of the grammar. */
    classify(msg) {
        const h = msg.headers ?? newHeaders();
        const code = h.get(ERROR_CODE_HEADER);
        if (code !== "") {
            this.term = h;
            throw new ServiceError(code, h.get(ERROR_HEADER));
        }
        const seqStr = h.get(HEADER_SEQ);
        const status = h.get(HEADER_STATUS);
        if (msg.data.length > 0) {
            if (status !== "" && seqStr !== "") {
                throw new ProtocolError("frame: content with a status");
            }
            const plain = this.caller.open(msg.data, this.key);
            if (seqStr === "") {
                // One reply is the whole answer.
                if (this.seq > 0) {
                    throw new ProtocolError("frame: one-shot after content");
                }
                this.done = true;
                this.term = h;
                return { kind: Kind.content, seq: 0, data: plain, headers: h };
            }
            const seq = parseSeq(seqStr);
            if (seq === undefined || seq !== this.seq + 1) {
                throw new GapError(`frame: sequence gap: got "${seqStr}" after ${this.seq}`);
            }
            this.seq = seq;
            return { kind: Kind.content, seq, data: plain, headers: h };
        }
        if (status !== "") {
            let last = 0;
            if (seqStr !== "") {
                const n = parseSeq(seqStr);
                if (n === undefined) {
                    throw new GapError(`frame: sequence gap: terminator sequence "${seqStr}"`);
                }
                last = n;
            }
            if (last !== this.seq) {
                throw new GapError(`frame: sequence gap: terminator says ${last}, read ${this.seq}`);
            }
            this.term = h;
            this.done = true;
            if (status !== STATUS_DONE) {
                throw new ProtocolError(`frame: status "${status}" without an error code`);
            }
            return { kind: Kind.terminator, seq: last, data: EMPTY, headers: h };
        }
        if (h.get(HEADER_PROGRESS) !== "") {
            return { kind: Kind.progress, seq: 0, data: EMPTY, headers: h };
        }
        throw new ProtocolError();
    }
}
/**
 * open seals the request toward the service's key — or the sealing key the
 * caller resolved itself — sends it with a reply inbox of its own inside the
 * connection's fence, and waits for the first frame. A cannot-open refusal
 * rediscovers the key by service name and retries once, unless a sealing key
 * was given, in which case rediscovering it is the caller's; any
 * other refusal before the first frame is thrown as a ServiceError; no
 * responder as nats-core's NoRespondersError. The signal governs the whole
 * stream.
 */
export async function open(nc, caller, req) {
    const idle = req.idle ?? DEFAULT_IDLE;
    const timeout = req.timeout ?? DEFAULT_TIMEOUT;
    const body = req.body ?? EMPTY;
    for (let attempt = 0;; attempt++) {
        const key = req.sealingKey ?? (await caller.discover(nc, req.service, timeout));
        const sealed = caller.sealToward(key, body);
        const inbox = newInbox(nc);
        const sub = nc.subscribe(inbox);
        const it = sub[Symbol.asyncIterator]();
        const h = newHeaders();
        if (req.headers) {
            for (const [k, values] of req.headers) {
                for (const v of values) {
                    h.append(k, v);
                }
            }
        }
        h.set(SEALED_BY, caller.publicKey());
        nc.publish(req.subject, sealed, { reply: inbox, headers: h });
        let first;
        try {
            first = await next(it, idle, req.signal);
        }
        catch (err) {
            sub.unsubscribe();
            throw err;
        }
        const status = first.headers?.code ?? 0;
        if (status !== 0) {
            sub.unsubscribe();
            if (status === NO_RESPONDERS) {
                throw new NoRespondersError(req.subject);
            }
            throw new ProtocolError(`frame: server status ${status}`);
        }
        const code = first.headers?.get(ERROR_CODE_HEADER) ?? "";
        if (code !== "") {
            sub.unsubscribe();
            if (code === CODE_CANNOT_OPEN && attempt === 0 && req.sealingKey === undefined) {
                caller.forget(req.service);
                continue;
            }
            throw new ServiceError(code, first.headers?.get(ERROR_HEADER) ?? "");
        }
        return new Reader(sub, it, caller, key, idle, req.signal, first);
    }
}
/**
 * collect reads a whole reply: the one-shot answer, or every content frame
 * concatenated, with the terminator's headers. Progress frames are skipped.
 * An error ends it as frames does; a caller that wants what was delivered
 * before an error reads the frames itself.
 */
export async function collect(r) {
    const chunks = [];
    let total = 0;
    for await (const f of r.frames()) {
        if (f.kind === Kind.content) {
            chunks.push(f.data);
            total += f.data.length;
        }
    }
    const data = new Uint8Array(total);
    let at = 0;
    for (const c of chunks) {
        data.set(c, at);
        at += c.length;
    }
    return { data, terminator: r.terminator() };
}
//# sourceMappingURL=frame.js.map